/**
 * The reasoning-effort chip's value — one store, two composers (#111).
 *
 * The chat pane and the skill editor each draw the chip, and the ticket's
 * decision is that they share one value: a user who turned effort down for
 * quick questions expects the skill editor to be on the same setting, not to
 * have a second dial they forgot about. So the value lives here rather than in
 * either pane's state, in the `sidebarStore` shape: the rules in a plain module
 * a test can hold to, a `subscribe`/`read` pair for `useSyncExternalStore`, and
 * every `localStorage` access guarded — a remembered preference is a
 * convenience, and losing it must never cost a render.
 *
 * Per user, not per thread: the agent pins the effort per *turn* (a resume
 * keeps the effort the interrupted turn ran at, like `agentMode`), and that is
 * the only per-turn memory it needs.
 */
import { DEFAULT_REASONING_EFFORT, isReasoningEffort, type ReasoningEffort } from "./models";

const STORAGE_KEY = "bimgo_reasoning_effort";

/** Somewhere to read and write strings — `localStorage`, or a stub in a test. */
export interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStore(): StringStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Touching `localStorage` itself throws where site data is blocked.
    return null;
  }
}

/**
 * What a stored value means. Only one of the four names counts; anything else
 * (an older shape, garbage, nothing) is the default — the agent's own, so a
 * bad entry costs nothing the user would notice.
 */
export function parseReasoningEffort(raw: string | null): ReasoningEffort {
  return isReasoningEffort(raw) ? raw : DEFAULT_REASONING_EFFORT;
}

export interface ReasoningEffortStore {
  read(): ReasoningEffort;
  set(next: ReasoningEffort): void;
  subscribe(listener: () => void): () => void;
}

export function createReasoningEffortStore(storage: StringStore | null): ReasoningEffortStore {
  let value: ReasoningEffort = DEFAULT_REASONING_EFFORT;
  try {
    value = parseReasoningEffort(storage?.getItem(STORAGE_KEY) ?? null);
  } catch {
    // A read that throws is the same as no stored value.
  }
  const listeners = new Set<() => void>();

  return {
    read: () => value,
    set(next) {
      if (!isReasoningEffort(next) || next === value) return;
      value = next;
      try {
        storage?.setItem(STORAGE_KEY, next);
      } catch {
        // Persisting is best-effort; the in-memory value still drives the UI.
      }
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The app's one store, over the browser's storage. */
export const reasoningEffortStore: ReasoningEffortStore = createReasoningEffortStore(browserStore());
